import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types } from 'mobx-state-tree';
import { api } from 'src/api';
import { isHttpClientCancel } from 'src/core/services/api/httpClient';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';
/** Statuses reported by the bill automation service. */
export const BILL_AUTOMATION_STATUS = {
  /** Landed from a dashboard upload, waiting for the dispatcher to hand it to OCR. */
  pending: 'Pending',
  processing: 'Processing',
  extracted: 'Extracted',
  validated: 'Validated',
  /** Extracted, but no property matched: it waits for a person, not for the pipeline. */
  needsProperty: 'NeedsProperty',
  failed: 'Failed',
  /** The sender was not trusted; the bill was never read (LB-5765). */
  rejected: 'Rejected',
  /** The company already holds this bill — same file, or same account + service period (LB-5779). */
  duplicate: 'Duplicate'
};

/**
 * Statuses the pipeline still moves on its own. Exported so apply billing polls exactly these too
 * rather than keeping its own list (LB-5919).
 */
export const POLLABLE_STATUSES = [BILL_AUTOMATION_STATUS.pending, BILL_AUTOMATION_STATUS.processing, BILL_AUTOMATION_STATUS.extracted];
const FINAL_STATUSES = [BILL_AUTOMATION_STATUS.validated, BILL_AUTOMATION_STATUS.needsProperty, BILL_AUTOMATION_STATUS.failed,
// Without these two, a duplicate or rejected bill reads as "processing" forever in the details
// view — nothing will ever move it, so it is final by definition.
BILL_AUTOMATION_STATUS.rejected, BILL_AUTOMATION_STATUS.duplicate];

/** A bill stops being polled after this many consecutive failed status requests. */
const MAX_FAILED_POLLS = 3;

/**
 * Whether a failed read was the service saying this bill does not exist, rather than the request
 * not getting through.
 *
 * Narrow on purpose. Anything that is not a stated 404 — a timeout, a 502, a request that never
 * left — leaves the bill exactly as it was, still reading as processing and still being retried.
 */
const isBillNotFound = error => {
  var _response;
  return (error === null || error === void 0 || (_response = error.response) === null || _response === void 0 ? void 0 : _response.status) === 404;
};

/**
 * Validation arrives through a Kafka event, so a bill can sit in Extracted indefinitely if that
 * consumer is down. Give up after roughly ten minutes instead of polling for the whole session.
 */
const MAX_POLLS_PER_BILL = 120;
/**
 * A note field as the service answered it: absent (an older service) keeps what is shown, null (no note) clears it.
 */
function noteField(response, key, current) {
  var _response$key;
  if (!response || !(key in response)) return current;
  return (_response$key = response[key]) !== null && _response$key !== void 0 ? _response$key : '';
}

/**
 * Whether an extraction payload actually says anything.
 *
 * An edit the service could not map comes back as an empty object rather than null, so a plain
 * `editedData ?? data` would render a blank bill instead of falling back to the extraction. Presence
 * is judged on content for that reason, not on nullishness.
 */
function hasAnyValue(payload) {
  if (!payload || typeof payload !== 'object') return false;
  return Object.values(payload).some(value => value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0));
}
const UploadedBill = types.model('UploadedBill', {
  billId: types.identifier,
  propertyId: types.optional(types.string, ''),
  /**
   * Every property the bill was uploaded for — one bill can cover several when a provider charges
   * them together. Holds `propertyId` alone for the ordinary bill.
   */
  propertyIds: types.optional(types.array(types.string), []),
  propertyAddress: types.optional(types.string, ''),
  fileName: types.optional(types.string, ''),
  /** Object URL of the uploaded file, so details can preview it without a file service round trip. */
  fileUrl: types.optional(types.string, ''),
  fileSizeMB: types.optional(types.number, 0),
  /** Utility types sent with the upload, so the row has utilities before extraction finishes. */
  requestedUtilityTypes: types.optional(types.array(types.string), []),
  status: types.optional(types.string, BILL_AUTOMATION_STATUS.processing),
  data: types.optional(types.frozen(), null),
  /**
   * The saved correction, as the service reports it (LB-5840). Kept next to `data` rather than
   * folded into it so the original stays auditable — and because the AP push modal weighs the two
   * apart when it judges whether an invoice number can be trusted.
   */
  editedData: types.optional(types.frozen(), null),
  /**
   * The revision an edit has to be made against. Every pipeline write bumps it, so a bill nobody
   * has edited still arrives well past 0; 0 means only that this bill has not been read yet.
   */
  version: types.optional(types.number, 0),
  /** The newest trail entry known, so a save can show itself without refetching the trail. */
  latestEdit: types.optional(types.frozen(), null),
  /** The fetched page of the trail, and how many entries there are in all. Null until asked for. */
  editHistory: types.optional(types.frozen(), null),
  /** Per property/utility allocation inputs (service dates, bill amount, CAD/GWD deductions). */
  allocationSettings: types.optional(types.frozen(), {}),
  errors: types.optional(types.frozen(), []),
  warnings: types.optional(types.frozen(), []),
  /**
   * The service's customer wording of `errors` and `warnings` (LB-5918), which is what a screen shows.
   * Null -- not empty -- until a service that sends them has answered: empty means "nothing to say",
   * while null tells the reader to fall back to the stored text an older service sends instead.
   */
  reasons: types.optional(types.frozen(), null),
  notices: types.optional(types.frozen(), null),
  /** Whether this bill, in Error, can be entered by hand (LB-6095); null for any other status. */
  handEntry: types.optional(types.frozen(), null),
  /** When the bill was entered by hand from its failed read (LB-6095): it has no read to go back to. */
  enteredByHandAt: types.optional(types.maybeNull(types.string), null),
  failedPollCount: types.optional(types.number, 0),
  pollCount: types.optional(types.number, 0),
  /**
   * The service answered 404 for this bill: it has been deleted, or the link names one that never
   * existed.
   *
   * Kept apart from `status`, which is what the SERVICE reports — there is no such status, and
   * inventing one would put a value in that enum no payload can ever carry. Kept apart from
   * `failedPollCount` too, and that is the point: a dropped request and a deleted bill both used to
   * land there, so the only thing either could do was stop polling and leave the view saying
   * "processing" for ever (LB-5897).
   */
  isMissing: types.optional(types.boolean, false),
  /** What the customer wrote to our team with the bill, who wrote it and when (LB-6056). Empty when nothing. */
  note: types.optional(types.string, ''),
  noteBy: types.optional(types.string, ''),
  noteAt: types.optional(types.string, '')
}).views(store => ({
  get isFinalStatus() {
    return FINAL_STATUSES.includes(store.status);
  },
  /** Every property the bill covers, as a plain array. Rows from before the set was reported know one. */
  get assignedPropertyIds() {
    return store.propertyIds.length ? [...store.propertyIds] : [store.propertyId].filter(Boolean);
  },
  /** Bills nothing will move on their own drop out of the polling sweep. */
  get isPollable() {
    return !store.isMissing && POLLABLE_STATUSES.includes(store.status) && store.failedPollCount < MAX_FAILED_POLLS && store.pollCount < MAX_POLLS_PER_BILL;
  },
  /** What the details view renders: the saved correction when there is one, else the extraction. */
  get effectiveData() {
    return hasAnyValue(store.editedData) ? store.editedData : store.data;
  },
  /** Whether this bill carries a correction, and so has an original worth offering to restore. */
  get isEdited() {
    return hasAnyValue(store.editedData);
  },
  /**
   * How long the extraction took, for the "Thought for X s" line: the service measures it and sends it
   * with the extraction, the figure the Avg extraction time tile averages. Not timed here: this page
   * only knows when it first saw the bill finished, which for a bill opened from the list is when its
   * details loaded (LB-5926).
   */
  get extractionSeconds() {
    var _store$data;
    const durationMs = (_store$data = store.data) === null || _store$data === void 0 ? void 0 : _store$data.extractionDurationMs;
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) return null;
    return Math.max(1, Math.round(durationMs / 1000));
  }
})).actions(store => ({
  applyStatusResponse(response) {
    var _response$propertyIds, _response$data, _response$editedData, _response$version, _response$errors, _response$warnings, _response$reasons, _response$notices, _response$handEntry, _response$enteredByHa;
    // The service reports which properties the bill belongs to, which is how a bill uploaded without
    // one gets its property once extraction matches the service address.
    store.propertyId = (response === null || response === void 0 ? void 0 : response.propertyId) || store.propertyId;
    if (response !== null && response !== void 0 && (_response$propertyIds = response.propertyIds) !== null && _response$propertyIds !== void 0 && _response$propertyIds.length) store.propertyIds.replace(response.propertyIds);
    store.status = (response === null || response === void 0 ? void 0 : response.status) || store.status;
    // Read at last. A bill this session uploaded had its name from the picker, so this only ever
    // mattered for the others — one opened from an email link, or a duplicate opened from the
    // list — and for those it was the difference between the modal knowing what kind of file it
    // was holding and guessing PDF (LB-5897). `||` so an answer without one cannot blank it.
    store.fileName = (response === null || response === void 0 ? void 0 : response.fileName) || store.fileName;
    // It answered about this bill, so it exists. Matters for a bill that 404ed while the service
    // was mid-deploy: without this it would stay written off for the rest of the session.
    store.isMissing = false;
    store.data = (_response$data = response === null || response === void 0 ? void 0 : response.data) !== null && _response$data !== void 0 ? _response$data : store.data;
    // `?? null` rather than the `?? store.x` the line above uses, and deliberately: somebody else
    // restoring the original has to clear it here too, or this client goes on rendering — and
    // saving from — a correction the bill no longer carries.
    store.editedData = (_response$editedData = response === null || response === void 0 ? void 0 : response.editedData) !== null && _response$editedData !== void 0 ? _response$editedData : null;
    // Kept when absent, so an older service that does not send one cannot reset a known version.
    store.version = (_response$version = response === null || response === void 0 ? void 0 : response.version) !== null && _response$version !== void 0 ? _response$version : store.version;
    store.errors = (_response$errors = response === null || response === void 0 ? void 0 : response.errors) !== null && _response$errors !== void 0 ? _response$errors : [];
    store.warnings = (_response$warnings = response === null || response === void 0 ? void 0 : response.warnings) !== null && _response$warnings !== void 0 ? _response$warnings : [];
    store.reasons = (_response$reasons = response === null || response === void 0 ? void 0 : response.reasons) !== null && _response$reasons !== void 0 ? _response$reasons : null;
    store.notices = (_response$notices = response === null || response === void 0 ? void 0 : response.notices) !== null && _response$notices !== void 0 ? _response$notices : null;
    // Kept when absent, so an older service that does not send one cannot blank a note already shown.
    store.note = noteField(response, 'note', store.note);
    store.noteBy = noteField(response, 'noteBy', store.noteBy);
    store.noteAt = noteField(response, 'noteAt', store.noteAt);
    store.handEntry = (_response$handEntry = response === null || response === void 0 ? void 0 : response.handEntry) !== null && _response$handEntry !== void 0 ? _response$handEntry : null;
    store.enteredByHandAt = (_response$enteredByHa = response === null || response === void 0 ? void 0 : response.enteredByHandAt) !== null && _response$enteredByHa !== void 0 ? _response$enteredByHa : null;
    store.failedPollCount = 0;
  },
  /**
   * Fold a save or a discard back in. The service answers both with the bill's new revision, so
   * the next edit is made against it without waiting for a poll — which matters because the poll
   * has already stopped for a bill in an editable status.
   *
   * `entry` is absent when the save changed nothing, and that is not an error: the version still
   * moves on and nothing is added to the trail.
   */
  applyEditResult(result) {
    store.version = result.version;
    if (!result.entry) return;
    store.latestEdit = result.entry;
    // Kept in step rather than refetched: the panel is open on this bill, and a stale total would
    // have it offer "show all 3" over four entries.
    if (store.editHistory) {
      store.editHistory = {
        entries: [result.entry, ...store.editHistory.entries],
        total: store.editHistory.total + 1
      };
    }
  },
  setEditHistory(history) {
    const incoming = history.entries[0];
    const known = store.latestEdit;

    // A read started before a save can land after it — the card asks for one entry as it mounts,
    // and nothing cancels that when somebody saves straight away. Such a response describes the
    // bill as it was, so it is dropped whole rather than in part: taking its entries would lose
    // the row the save just recorded, and taking its total would undercount the trail by one.
    if (known && (!incoming || incoming.billVersionAfter < known.billVersionAfter)) return;
    store.editHistory = history;
    store.latestEdit = incoming !== null && incoming !== void 0 ? incoming : known;
  },
  setAllocationSettings(settings) {
    store.allocationSettings = settings;
  },
  registerPollAttempt() {
    store.pollCount += 1;
  },
  registerPollFailure() {
    let isMissing = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    store.failedPollCount += 1;
    // Only a stated 404. Every other failure stays a failure to ask -- a dropped request, a
    // gateway having a moment -- and must go on reading as "still processing", because telling
    // somebody their bill is gone over a flicker of bad wifi is the worse of the two mistakes.
    if (isMissing) store.isMissing = true;
  }
}));

/**
 * Reads one bill's status and extraction payload. Only the bill being looked at is read: the payload
 * is what the details view needs, and nothing else shows it, so sweeping every bill known would cost
 * a request each for answers nobody is waiting for.
 */
const pollUploadedBillAsync = createAsync(body => {
  return async function pollUploadedBill(options) {
    try {
      this.registerPollAttempt(body.billId);
      const resp = await api.BillAutomationBills.getBill(body.billId, options);
      if (resp !== null && resp !== void 0 && resp.isSuccessful && resp !== null && resp !== void 0 && resp.response) this.applyBillStatus(body.billId, resp.response);else this.registerPollFailure(body.billId);
    } catch (error) {
      if (isHttpClientCancel(error)) throw error;
      // A flaky status request should not raise a toast every few seconds.
      //
      // A 404 is not flaky, though, and telling the two apart is the whole of the fix: the client
      // rejects on 4xx, so the status is right here to be read (LB-5897).
      this.registerPollFailure(body.billId, isBillNotFound(error));
    }
  };
});

/**
 * Reads the bill back after a write, and folds it in the same way a poll would.
 *
 * The service merges an edit onto the payload it already holds — recalculating subtotals and
 * carrying the allocation ids the narrow request never sends — so what it stores afterwards is not
 * what was posted. Assuming otherwise would leave the editor rendering a payload the bill does not
 * have, and the same read refreshes the version the next edit must be made against.
 */
async function rereadBill(store, billId, options) {
  try {
    const resp = await api.BillAutomationBills.getBill(billId, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp !== null && resp !== void 0 && resp.response) {
      store.applyBillStatus(billId, resp.response);
      return true;
    }
  } catch (_unused) {
    // Swallowed on purpose. The write has already committed, and letting this reach the async model
    // would report the whole action as failed: the caller would leave the draft open and say nothing
    // saved, while the version has moved on and the next attempt is refused as stale.
  }
  return false;
}

/**
 * Saves a correction (LB-5840). A refusal — the bill was allocated or pushed while the drawer was
 * open, or somebody else edited it first — arrives as a resolved 409 carrying the service's own
 * wording, so it is returned for the caller to show rather than raised as a failure.
 */
const saveBillEditAsync = createAsync(body => {
  return async function saveBillEdit(options) {
    const resp = await api.BillAutomationBills.saveBillEdit(body, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful) {
      if (resp.response) this.applyEditResult(body.billId, resp.response);
      return _objectSpread(_objectSpread({}, resp), {}, {
        isStale: !(await rereadBill(this, body.billId, options))
      });
    }
    return resp;
  };
});

/** Throws every edit away and returns the bill to what was extracted. Refused like a save. */
const discardBillEditAsync = createAsync(body => {
  return async function discardBillEdit(options) {
    const resp = await api.BillAutomationBills.discardBillEdit(body, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful) {
      if (resp.response) this.applyEditResult(body.billId, resp.response);
      return _objectSpread(_objectSpread({}, resp), {}, {
        isStale: !(await rereadBill(this, body.billId, options))
      });
    }
    return resp;
  };
});

/**
 * Who changed what. Silent: the trail decorates a page whose job is the bill itself, so a panel
 * that cannot load should say so in place rather than raise a toast over the bill being read.
 */
const getBillEditHistoryAsync = createSilentAsync(body => {
  return async function getBillEditHistory(options) {
    const resp = await api.BillAutomationBills.getBillEditHistory(body, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp !== null && resp !== void 0 && resp.response) this.setEditHistory(body.billId, resp.response);
    return resp;
  };
});

/**
 * Bills uploaded in the current session. Runtime only: reloading the app clears them,
 * so nothing here is treated as a persisted record.
 */
const UploadedBillsStore = types.model('UploadedBillsStore', {
  bills: types.optional(types.array(UploadedBill), []),
  discardBillEditAsync,
  getBillEditHistoryAsync,
  pollUploadedBillAsync,
  saveBillEditAsync
}).actions(store => ({
  addUploadedBill(bill) {
    var _bill$propertyId, _bill$propertyIds, _bill$propertyAddress, _bill$fileName, _bill$fileUrl, _bill$fileSizeMB, _bill$requestedUtilit, _bill$data, _bill$errors, _bill$warnings, _bill$reasons, _bill$notices;
    const existing = store.bills.find(i => i.billId === bill.billId);
    if (existing) {
      existing.applyStatusResponse(bill);
      return;
    }
    store.bills.unshift({
      billId: bill.billId,
      propertyId: (_bill$propertyId = bill.propertyId) !== null && _bill$propertyId !== void 0 ? _bill$propertyId : '',
      propertyIds: (_bill$propertyIds = bill.propertyIds) !== null && _bill$propertyIds !== void 0 ? _bill$propertyIds : bill.propertyId ? [bill.propertyId] : [],
      propertyAddress: (_bill$propertyAddress = bill.propertyAddress) !== null && _bill$propertyAddress !== void 0 ? _bill$propertyAddress : '',
      fileName: (_bill$fileName = bill.fileName) !== null && _bill$fileName !== void 0 ? _bill$fileName : '',
      fileUrl: (_bill$fileUrl = bill.fileUrl) !== null && _bill$fileUrl !== void 0 ? _bill$fileUrl : '',
      fileSizeMB: (_bill$fileSizeMB = bill.fileSizeMB) !== null && _bill$fileSizeMB !== void 0 ? _bill$fileSizeMB : 0,
      requestedUtilityTypes: (_bill$requestedUtilit = bill.requestedUtilityTypes) !== null && _bill$requestedUtilit !== void 0 ? _bill$requestedUtilit : [],
      status: bill.status || BILL_AUTOMATION_STATUS.processing,
      data: (_bill$data = bill.data) !== null && _bill$data !== void 0 ? _bill$data : null,
      errors: (_bill$errors = bill.errors) !== null && _bill$errors !== void 0 ? _bill$errors : [],
      warnings: (_bill$warnings = bill.warnings) !== null && _bill$warnings !== void 0 ? _bill$warnings : [],
      reasons: (_bill$reasons = bill.reasons) !== null && _bill$reasons !== void 0 ? _bill$reasons : null,
      notices: (_bill$notices = bill.notices) !== null && _bill$notices !== void 0 ? _bill$notices : null
    });
  },
  removeUploadedBill(billId) {
    const bill = store.bills.find(i => i.billId === billId);
    if (!bill) return;
    const fileUrl = bill.fileUrl;
    store.bills.remove(bill);
    // One upload can cover several properties, so the object URL is shared by all of its rows.
    if (fileUrl && !store.bills.some(i => i.fileUrl === fileUrl)) URL.revokeObjectURL(fileUrl);
  },
  applyBillStatus(billId, response) {
    var _store$bills$find;
    (_store$bills$find = store.bills.find(i => i.billId === billId)) === null || _store$bills$find === void 0 || _store$bills$find.applyStatusResponse(response);
  },
  applyEditResult(billId, result) {
    var _store$bills$find2;
    (_store$bills$find2 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find2 === void 0 || _store$bills$find2.applyEditResult(result);
  },
  setEditHistory(billId, history) {
    var _store$bills$find3;
    (_store$bills$find3 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find3 === void 0 || _store$bills$find3.setEditHistory(history);
  },
  setAllocationSettings(billId, settings) {
    var _store$bills$find4;
    (_store$bills$find4 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find4 === void 0 || _store$bills$find4.setAllocationSettings(settings);
  },
  registerPollAttempt(billId) {
    var _store$bills$find5;
    (_store$bills$find5 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find5 === void 0 || _store$bills$find5.registerPollAttempt();
  },
  registerPollFailure(billId) {
    var _store$bills$find6;
    let isMissing = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    (_store$bills$find6 = store.bills.find(i => i.billId === billId)) === null || _store$bills$find6 === void 0 || _store$bills$find6.registerPollFailure(isMissing);
  }
})).views(store => ({
  /** Whether reading this bill again is worth it: the pipeline still has it, and it still answers. */
  isWorthReadingAgain(billId) {
    var _store$bills$find7;
    return Boolean((_store$bills$find7 = store.bills.find(bill => bill.billId === billId)) === null || _store$bills$find7 === void 0 ? void 0 : _store$bills$find7.isPollable);
  }
}));
export const uploadedBills = types.optional(UploadedBillsStore, {});